import { ResponsiveLine } from "@nivo/line";
import { Box, Flex, Text } from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { CSSProperties, useEffect, useMemo, useState } from "react";
import { CHART_COLORS, getChartTheme } from "./chartTheme";
import { yTickValues } from "./chartHelpers";

export interface LineChartSeries {
  id: string;
  label: string;
  data: Array<{ x: string | number; y: number }>;
}

/** Short axis label: 1_500 → "1.5k", 2_000_000 → "2m". Tooltips keep the full number. */
function compactNumber(value: number): string {
  const n = Number(value);
  const abs = Math.abs(n);
  const strip = (x: number) => x.toFixed(1).replace(/\.0$/, "");
  if (abs >= 1e9) return `${strip(n / 1e9)}b`;
  if (abs >= 1e6) return `${strip(n / 1e6)}m`;
  if (abs >= 1e3) return `${strip(n / 1e3)}k`;
  return String(n);
}

export interface LineChartProps {
  series: LineChartSeries[];
  title?: string;
  height?: number;
  stacked?: boolean;
  enableArea?: boolean;
  xAxisLabel?: string;
  yAxisLabel?: string;
  /**
   * When true, hovering anywhere on a given x shows one shared tooltip listing every
   * series value at that x (Nivo "slice" mode) instead of a per-point tooltip.
   */
  enableSlices?: boolean;
  /**
   * When true, renders a clickable legend below the chart. Clicking a series toggles
   * its line on/off. Colours stay pinned per series, so hiding one never recolours the rest.
   */
  showLegend?: boolean;
  /** When true, formats Y-axis ticks and tooltip values with thousands separators (e.g. 1,234,567). */
  commaSeparated?: boolean;
  /** When true, forces whole-number Y-axis ticks/gridlines (no fractional counts like 0.5). */
  integerYAxis?: boolean;
}

const LineChart = ({
  series,
  title,
  height = 250,
  stacked = true,
  enableArea = false,
  xAxisLabel: _xAxisLabel = "Time",
  yAxisLabel: _yAxisLabel = "Value",
  enableSlices = false,
  showLegend = false,
  commaSeparated = false,
  integerYAxis = false,
}: LineChartProps) => {
  const { t } = useTranslation();
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark"),
  );
  // Series ids the user has toggled off via the legend.
  const [hidden, setHidden] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  // Pin a colour to each series id from the full list so toggling never remaps colours.
  const colorFor = useMemo(() => {
    const map: Record<string, string> = {};
    series.forEach((s, i) => {
      map[s.id] = CHART_COLORS[i % CHART_COLORS.length];
    });
    return map;
  }, [series]);

  const chartData = useMemo(() => {
    if (!series || series.length === 0) return [];
    return series
      .filter((s) => !hidden.has(s.id))
      .map((s) => ({ id: s.id, data: s.data }));
  }, [series, hidden]);

  const toggle = (id: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Whole-number ticks when requested (e.g. active-user counts): derive integer tick
  // values from the visible data so the axis never shows fractional gridlines.
  const yTicks: number[] | number = integerYAxis
    ? yTickValues(
        chartData.flatMap((s) => s.data.map((d) => Number(d.y) || 0)),
        5,
      )
    : 5;

  if (!series || series.length === 0) {
    return (
      <Box textAlign="center">
        {title && (
          <Box fontWeight="bold" mb={4}>
            {title}
          </Box>
        )}
        <Box color="gray.500">{t("app.common.noDataAvailable")}</Box>
      </Box>
    );
  }

  // Nivo only styles its built-in tooltips; a custom tooltip element needs its own
  // (theme-aware) container background, matching the widget charts.
  const tooltipStyle: CSSProperties = {
    ...getChartTheme(isDark).tooltip.container,
    whiteSpace: "nowrap",
  };

  const swatch = (color: string): CSSProperties => ({
    width: 10,
    height: 10,
    borderRadius: 2,
    background: color,
    display: "inline-block",
    flexShrink: 0,
  });

  return (
    <Box>
      {title && (
        <Box fontWeight="bold" mb={4} fontSize="lg">
          {title}
        </Box>
      )}
      <Box height={`${height}px`}>
        {chartData.length === 0 ? (
          <Flex h="full" align="center" justify="center">
            <Text color="gray.500">{t("app.common.noDataAvailable")}</Text>
          </Flex>
        ) : (
          <ResponsiveLine
            key={isDark ? "dark" : "light"}
            data={chartData}
            theme={getChartTheme(isDark)}
            colors={(d: any) => colorFor[d.id] ?? CHART_COLORS[0]}
            margin={{
              top: 20,
              right: 20,
              bottom: 40,
              left: 56,
            }}
            yScale={{ type: "linear", stacked }}
            curve="monotoneX"
            enableArea={enableArea || stacked}
            enablePoints={true}
            pointSize={6}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            useMesh={!enableSlices}
            enableSlices={enableSlices ? "x" : false}
            animate={false}
            enableGridX={false}
            enableGridY={true}
            gridYValues={yTicks}
            yFormat={
              commaSeparated ? (v) => Number(v).toLocaleString() : undefined
            }
            axisLeft={{
              tickValues: yTicks,
              // Short ticks (1k / 2m); tooltips keep the full comma-formatted number via yFormat.
              format: commaSeparated
                ? (v) => compactNumber(Number(v))
                : undefined,
            }}
            sliceTooltip={({ slice }: any) => (
              <div style={tooltipStyle}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {slice.points[0]?.data?.xFormatted}
                </div>
                {[...slice.points]
                  .sort((a: any, b: any) =>
                    String(a.seriesId).localeCompare(String(b.seriesId)),
                  )
                  .map((point: any) => (
                    <div
                      key={point.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 2,
                      }}
                    >
                      <span style={swatch(point.seriesColor)} />
                      <span>
                        {point.seriesId}: {point.data.yFormatted}
                      </span>
                    </div>
                  ))}
              </div>
            )}
            tooltip={({ point }) => (
              <div style={tooltipStyle}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {point.data.xFormatted}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={swatch(point.seriesColor)} />
                  <span>
                    {chartData.length > 1 ? `${point.seriesId}: ` : ""}
                    {point.data.yFormatted}
                  </span>
                </div>
              </div>
            )}
          />
        )}
      </Box>
      {showLegend && (
        <Flex wrap="wrap" gap={3} mt={3} justify="center">
          {series.map((s) => {
            const isHidden = hidden.has(s.id);
            return (
              <Flex
                key={s.id}
                align="center"
                gap={1.5}
                onClick={() => toggle(s.id)}
                cursor="pointer"
                opacity={isHidden ? 0.4 : 1}
                title={s.label}
              >
                <span style={swatch(colorFor[s.id])} />
                <Text
                  fontSize="xs"
                  style={{
                    textDecoration: isHidden ? "line-through" : "none",
                  }}
                >
                  {s.label}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      )}
    </Box>
  );
};

export default LineChart;
